import { useState } from "react";
import { Copy, Check, Plug, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { NETWORK_CONFIG } from "@/constants/network";
import { useWallet } from "@/contexts/WalletContext";
import { UptimeBar } from "@/components/status/UptimeBar";
import { RpcSnippets } from "@/components/status/RpcSnippets";

const FIELDS = [
  { key: "rpc", label: "RPC URL", value: NETWORK_CONFIG.rpcUrls[0], testId: "status-copy-rpc" },
  { key: "chain", label: "Chain ID", value: String(NETWORK_CONFIG.chainId), testId: "status-copy-chainid" },
  { key: "symbol", label: "Currency", value: NETWORK_CONFIG.nativeCurrency.symbol, testId: "status-copy-symbol" },
  { key: "explorer", label: "Explorer", value: NETWORK_CONFIG.blockExplorerUrls[0], testId: "status-copy-explorer" },
];

const CopyField = ({ label, value, testId, primary }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  const Icon = copied ? Check : Copy;
  return (
    <button data-testid={testId} onClick={copy} title={`Copy ${label}`}
      className={`group text-left w-full flex items-center justify-between gap-3 rounded-md bg-[#050505] border border-[#2A2411] hover:border-[#D4AF37]/50 transition-colors ${primary ? "p-4" : "p-3"}`}>
      <span className="min-w-0">
        <span className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</span>
        <span className={`block font-mono truncate ${primary ? "text-base sm:text-lg text-[#D4AF37]" : "text-sm text-zinc-200"}`}>{value}</span>
      </span>
      <Icon size={primary ? 18 : 14} className={`shrink-0 transition-transform group-hover:scale-110 ${copied ? "text-green-400" : "text-[#D4AF37]"}`} />
    </button>
  );
};

export const LiveRpcCard = ({ online }) => {
  const { addFSCNetwork } = useWallet();
  const [rpc, ...rest] = FIELDS;
  return (
    <Card data-testid="status-live-rpc" className="border-[#D4AF37]/30 bg-[#0A0A0A]">
      <CardContent className="p-5 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Plug size={16} className="text-[#D4AF37]" />
            <h2 className="font-display text-base font-semibold">Live RPC Endpoint</h2>
            <span data-testid="status-live-rpc-state" className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${online ? "text-green-400 border-green-500/30 bg-green-500/10" : "text-red-400 border-red-500/30 bg-red-500/10"}`}>
              {online ? "Live" : "Offline"}
            </span>
          </div>
          <Button data-testid="status-add-network-btn" size="sm" onClick={addFSCNetwork}
            className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
            <Wallet size={14} className="mr-1.5" /> Add to MetaMask
          </Button>
        </div>
        <CopyField {...rpc} primary />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {rest.map((f) => <CopyField key={f.key} {...f} />)}
        </div>
        <UptimeBar />
        <RpcSnippets />
        <p className="text-[11px] text-zinc-500">Use these settings in MetaMask, ethers.js, Hardhat or Foundry. Each IP can send up to 300 requests per minute.</p>
      </CardContent>
    </Card>
  );
};
